import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getNews } from '../../services/api/news';
import type { NewsItem } from '../../types/api';
import NewsModal from './NewsModal';

const NewsBar: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await getNews();
      const activeNews = response.news
        .filter((item: NewsItem) => item.active)
        .sort((a: NewsItem, b: NewsItem) => (b.priority || 0) - (a.priority || 0));
      setNews(activeNews);
    } catch (error) {
      console.error('Error fetching news:', error);
      setError('Failed to load news updates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (news.length > 0) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % news.length);
      }, 5000);

      return () => clearInterval(timer);
    }
  }, [news.length]);

  const navigate = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentIndex((prev) => (prev - 1 + news.length) % news.length);
    } else {
      setCurrentIndex((prev) => (prev + 1) % news.length);
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-12">
            <div className="animate-pulse text-gray-400">Loading news...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || news.length === 0) {
    return null;
  }

  const currentNews = news[currentIndex];

  return (
    <>
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-12 sm:h-14">
            <button
              onClick={() => navigate('prev')}
              className="p-1 hover:bg-gray-200 rounded-full transition flex-shrink-0"
              aria-label="Previous news"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
            </button>

            <button
              onClick={() => setShowModal(true)}
              className="flex-1 px-2 sm:px-4 hover:bg-gray-200/50 transition-colors rounded-md mx-1 sm:mx-2"
            >
              <div className="text-xs sm:text-sm text-gray-600 text-center truncate">
                <span className="font-medium">{currentNews.title}</span>
                <span className="hidden sm:inline">
                  <span className="mx-2">|</span>
                  <span>{currentNews.content.slice(0, 100)}...</span>
                </span>
              </div>
            </button>

            <button
              onClick={() => navigate('next')}
              className="p-1 hover:bg-gray-200 rounded-full transition flex-shrink-0"
              aria-label="Next news"
            >
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      <NewsModal
        news={currentNews}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};

export default NewsBar;